import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InfoIcon, SaveIcon, SendIcon, Sparkles, Variable, ShoppingCart, Star, Facebook, Instagram } from 'lucide-react';
import CreateTemplateDialog from '@/components/sms/templates/CreateTemplateDialog';
import PersonalizationOptions from '@/components/sms/campaign/composition/PersonalizationOptions';
import PromoCodeSelector from '@/components/sms/promocodes/PromoCodeSelector';
import { CampaignData } from '@/hooks/sms/campaign/types';
import { toast } from '@/hooks/use-toast';
import { sendTestMessage, getTemplates, createTemplate } from '@/services/smsApi';
import { OPT_OUT_MESSAGE, getOptOutReceivedLength } from '@/utils/smsUtils';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { replacePromoPlaceholders } from '@/utils/sms/placeholderUtils';
import { PromoCode } from '@/hooks/sms/types';
import EnhancedMessageCharacterCount from '@/components/sms/shared/counters/EnhancedMessageCharacterCount';
import { useAuth } from '@/context/auth';

interface MessageCompositionProps {
  campaignData: CampaignData;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  userRestaurants: any[];
  allowDynamicVariables?: boolean;
  hidePromoCode?: boolean;
}

interface SmsTemplate {
  id: string;
  name: string;
  content: string;
}

interface MessageStats {
  characters: number;
  segments: number;
  remaining: number;
  hasUnicode: boolean;
  totalCharacters?: number;
  optOutLength?: number;
}

const SMS_CHAR_LIMIT = 160;
const SMS_CHAR_LIMIT_UNICODE = 70;

const MessageComposition: React.FC<MessageCompositionProps> = ({
  campaignData,
  updateCampaignData,
  userRestaurants,
  allowDynamicVariables = false,
  hidePromoCode = false
}) => {
  const { user } = useAuth();
  const { restaurantDetails } = useRestaurantContext();
  const restaurants = restaurantDetails || userRestaurants || [];
  
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('none');
  const [isSaveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  
  // Shared restaurant selection for both PersonalizationOptions and PromoCodeSelector
  const [selectedPersonalizationRestaurantId, setSelectedPersonalizationRestaurantId] = useState<string>(
    restaurants.length === 1 ? String(restaurants[0].id) : ''
  );
  const [messageStats, setMessageStats] = useState<MessageStats>({
    characters: 0,
    segments: 1,
    remaining: SMS_CHAR_LIMIT,
    hasUnicode: false
  });
  
  const containsUnicode = (text: string): boolean => {
    for (let i = 0; i < text.length; i++) {
      if (text.charCodeAt(i) > 127) {
        return true;
      }
    }
    return false;
  };
  
  const calculateMessageStats = useCallback((message: string) => {
    // Use received opt-out length for accurate character counting
    // Kudosity: We send 20 chars but customers receive 22 chars
    const optOutReceivedLength = getOptOutReceivedLength(); // 22 chars
    const fullMessage = message + 'X'.repeat(optOutReceivedLength); // Placeholder for accurate calculation

    console.log(`Message length: ${message.length}`);
    console.log(`Opt-out sent: ${OPT_OUT_MESSAGE.length} chars, received: ${optOutReceivedLength} chars`);
    console.log(`Full message length: ${fullMessage.length}`);

    const hasUnicode = containsUnicode(fullMessage);
    const charLimit = hasUnicode ? SMS_CHAR_LIMIT_UNICODE : SMS_CHAR_LIMIT;
    const messageOnlyChars = message.length;
    const totalCharacters = fullMessage.length;
    const segments = Math.ceil(totalCharacters / charLimit) || 1;
    const remaining = (segments * charLimit) - totalCharacters;

    setMessageStats({
      characters: messageOnlyChars,
      totalCharacters,
      segments,
      remaining,
      hasUnicode,
      optOutLength: optOutReceivedLength // 22 chars (Kudosity system)
    });
  }, []);
  
  useEffect(() => {
    calculateMessageStats(campaignData.message);
  }, [campaignData.message, calculateMessageStats]);
  
  useEffect(() => {
    const fetchTemplates = async () => {
      if (userRestaurants.length === 0) return;
      
      try {
        setLoadingTemplates(true);
        const restaurantId = userRestaurants[0].id;
        const response = await getTemplates(restaurantId);
        
        const validTemplates = (response.templates || [])
          .filter(template => 
            template && typeof template === 'object' && 
            template.id !== undefined && template.id !== null && template.id !== '' &&
            template.name
          )
          .map(template => ({
            ...template,
            id: String(template.id),
            name: template.name || "Unnamed Template",
            content: template.content || ""
          }));
          
        console.log(`Loaded ${validTemplates.length} valid templates`);
        setTemplates(validTemplates);
      } catch (error) {
        console.error('Error loading templates:', error);
        setTemplates([]);
        toast({
          title: 'Error loading templates',
          description: 'Could not load message templates. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setLoadingTemplates(false);
      }
    };
    
    fetchTemplates();
  }, [userRestaurants]);
  
  const handleTemplateSelect = (templateId: string) => {
    if (templateId === 'none') {
      setSelectedTemplate('none');
      return;
    }
    
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      updateCampaignData({ message: template.content });
    }
  };
  
  const insertVariable = (variable: string) => {
    const variableText = variable;
    const message = campaignData.message;
    const cursorPos = document.activeElement instanceof HTMLTextAreaElement
      ? document.activeElement.selectionStart
      : message.length;

    const newMessage = message.substring(0, cursorPos) +
      variableText +
      message.substring(cursorPos);

    updateCampaignData({ message: newMessage });
  };

  const handlePromoCodeSelect = (code: string, codeId: number, promoDetails?: PromoCode) => {
    if (promoDetails && campaignData.message.includes('[CODE]')) {
      // Smart replacement for placeholders
      const updatedMessage = replacePromoPlaceholders(campaignData.message, promoDetails);

      updateCampaignData({
        message: updatedMessage,
        promo_code_id: codeId,
        attribution_window_days: 7
      });

      // Show success toast
      toast({
        title: 'Promo code applied',
        description: `Replaced placeholders with promo code "${code}"`,
      });
    } else if (promoDetails) {
      // No [CODE] placeholder found, insert at cursor position
      insertVariable(code);

      updateCampaignData({
        promo_code_id: codeId,
        attribution_window_days: 7
      });

      toast({
        title: 'Promo code inserted',
        description: 'Added promo code to message',
      });
    } else {
      // Fallback to old behavior if no promo details
      insertVariable(code);
      updateCampaignData({
        promo_code_id: codeId,
        attribution_window_days: 7
      });
    }
  };
  
  const handleSendTest = async () => {
    if (!campaignData.testPhoneNumber) {
      toast({
        title: 'Phone number required',
        description: 'Please enter a phone number to send the test message to.',
        variant: 'destructive'
      });
      return;
    }
    
    if (!campaignData.message) {
      toast({
        title: 'Message required',
        description: 'Please enter a message to send.',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSendingTest(true);
      const restaurantId = userRestaurants[0].id;
      await sendTestMessage(
        restaurantId,
        campaignData.message,
        campaignData.testPhoneNumber
      );
      
      toast({
        title: 'Test message sent',
        description: `A test message has been sent to ${campaignData.testPhoneNumber}`,
      });
    } catch (error) {
      console.error('Error sending test message:', error);
      toast({
        title: 'Error sending test',
        description: 'Could not send test message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSendingTest(false);
    }
  };
  
  // Handle saving the current message as a template
  const handleSaveTemplate = async (template: { name: string; content: string; category: string }) => {
    try {
      const restaurantId = userRestaurants.length > 0 ? userRestaurants[0].id : null;

      if (!restaurantId) {
        toast({
          title: 'Error saving template',
          description: 'No restaurant selected.',
          variant: 'destructive'
        });
        return;
      }

      // Get the organisation ID if available
      let organisationId = null;
      // Try to find organisation_id from the first restaurant
      if (userRestaurants.length > 0 && userRestaurants[0].organisation_id) {
        organisationId = userRestaurants[0].organisation_id;
      }

      // Calculate estimated SMS units based on message content
      const hasUnicode = containsUnicode(template.content);
      const charLimit = hasUnicode ? SMS_CHAR_LIMIT_UNICODE : SMS_CHAR_LIMIT;
      const segments = Math.ceil(template.content.length / charLimit) || 1;

      // Format the data using snake_case as expected by the backend
      const templateData = {
        restaurant_id: restaurantId,
        organisation_id: organisationId, // Add organisation_id when available
        template_name: template.name,
        template_content: template.content,
        category: template.category,
        estimated_units: segments // Add estimated units based on segments
      };

      console.log('Creating template with data:', templateData);

      // Create the template using the API service
      const result = await createTemplate(templateData);

      console.log('Template creation result:', result);

      // Refresh templates list
      const refreshedTemplates = await getTemplates(restaurantId);
      if (refreshedTemplates?.templates) {
        const validTemplates = refreshedTemplates.templates
          .filter(t => t && typeof t === 'object' && t.id && t.name)
          .map(t => ({
            ...t,
            id: String(t.id),
            name: t.name || "Unnamed Template",
            content: t.content || ""
          }));

        setTemplates(validTemplates);
      }

      toast({
        title: 'Template saved',
        description: 'Your message has been saved as a template.',
      });

      // Close the dialog
      setSaveTemplateDialogOpen(false);

      // Select the newly created template if available
      if (result?.template?.id) {
        setSelectedTemplate(String(result.template.id));
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error saving template',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive'
      });
    }
  };
  
  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle>Compose Message</CardTitle>
        <CardDescription>
          Create your message content and test before sending
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="template">Load from Template</Label>
            <Select
              value={selectedTemplate}
              onValueChange={handleTemplateSelect}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a template or start from scratch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates.map(template => (
                  <SelectItem 
                    key={template.id} 
                    value={template.id || `template-${Math.random().toString(36).substring(2)}`}
                  >
                    {template.name || "Unnamed Template"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {loadingTemplates 
                ? 'Loading templates...' 
                : templates.length === 0 
                  ? 'No templates available. You can create templates in the Templates section.' 
                  : 'Select a template to pre-fill your message content'}
            </p>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="message">Message Content *</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setSaveTemplateDialogOpen(true)}
                className="gap-1"
                disabled={!campaignData.message.trim()}
              >
                <SaveIcon className="h-3.5 w-3.5" />
                Save as Template
              </Button>
            </div>
            <Textarea 
              id="message"
              rows={6}
              value={campaignData.message}
              onChange={e => updateCampaignData({ message: e.target.value })}
              placeholder="Type your message here..."
              className="resize-none"
              maxLength={1600}
            />
          </div>

          {/* Character counter - use enhanced for dynamic variables, simple for campaigns */}
          {allowDynamicVariables ? (
            <EnhancedMessageCharacterCount
              message={campaignData.message}
              organisationId={user?.organisationId}
              includeOptOut={true}
              showVariableList={true}
              className="mt-2"
            />
          ) : (
            <>
              <div className="text-right text-sm mt-2">
                <span className={messageStats.remaining < 10 ? 'text-destructive' : 'text-muted-foreground'}>
                  {messageStats.characters} characters
                  {messageStats.optOutLength && ` + ${messageStats.optOutLength} (opt-out message)`}
                  {' = '}
                  {messageStats.totalCharacters} total
                  {messageStats.segments > 1 ? ` (${messageStats.segments} SMS segments)` : ''}
                  {' - '}
                  {messageStats.remaining} characters remaining in current segment
                </span>
              </div>

              {messageStats.hasUnicode && (
                <Alert className="mt-2">
                  <InfoIcon className="h-4 w-4" />
                  <AlertDescription>
                    Your message contains special characters, which reduces the character limit per SMS segment to 70 (standard is 160).
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
          
          <PersonalizationOptions 
            onInsertPersonalization={insertVariable}
            restaurants={restaurants}
            messageContent={campaignData.message}
            selectedRestaurantId={selectedPersonalizationRestaurantId}
            onRestaurantChange={setSelectedPersonalizationRestaurantId}
            allowDynamicVariables={allowDynamicVariables}
          />
          
          {!hidePromoCode && (
            <div className="space-y-2 border-t pt-4">
              <Label>Promo Code</Label>
              <div className="flex gap-2">
                <PromoCodeSelector
                  restaurantId={selectedPersonalizationRestaurantId || undefined}
                  onSelectCode={handlePromoCodeSelect}
                  messageContent={campaignData.message}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedPersonalizationRestaurantId 
                  ? "Insert a promo code to track campaign effectiveness and calculate ROI"
                  : "Select a restaurant in the Personalization Options above to access promo codes"
                }
              </p>
            </div>
          )}
        </div>
        
        <Separator />
        
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Send Test Message</h3>
          
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-grow">
              <Label htmlFor="test-phone-number">Phone Number</Label>
              <Input
                id="test-phone-number"
                placeholder="0221234567 or +64221234567"
                value={campaignData.testPhoneNumber}
                onChange={e => updateCampaignData({ testPhoneNumber: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={handleSendTest}
                disabled={sendingTest || !campaignData.testPhoneNumber || !campaignData.message}
                className="gap-2"
              >
                {sendingTest && <span className="animate-spin">◌</span>}
                <SendIcon className="h-4 w-4" />
                Send Test
              </Button>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Send a test message to see exactly how your campaign will look when delivered.
          </p>
        </div>
      </CardContent>
    </Card>
    
    {/* Template Save Dialog */}
    <CreateTemplateDialog
      isOpen={isSaveTemplateDialogOpen}
      onOpenChange={setSaveTemplateDialogOpen}
      onCreateTemplate={handleSaveTemplate}
      restaurantId={userRestaurants.length > 0 ? userRestaurants[0].id : undefined}
      onSuccess={() => {}}
      initialContent={campaignData.message}
      templateToEdit={{
        id: '',
        name: '',
        content: campaignData.message,
        category: 'Promotional',
        restaurant_id: userRestaurants.length > 0 ? userRestaurants[0].id : ''
      }}
      restaurants={restaurants}
    />
  </>
);
};

export default MessageComposition;
